export const VEHICLE_SPEC_FIELDS = [
  { key: "engine", label: "Engine" },
  { key: "displacement", label: "Displacement" },
  { key: "cylinders", label: "Cylinders" },
  { key: "aspiration", label: "Aspiration" },
  { key: "power", label: "Power" },
  { key: "torque", label: "Torque" },
  { key: "transmission", label: "Transmission" },
  { key: "drivetrain", label: "Drivetrain" },
  { key: "weight", label: "Weight" },
  { key: "fuelType", label: "Fuel Type" }
];

export const createEmptyVehicleSpecs = () => ({
  engine: "",
  displacement: "",
  cylinders: "",
  aspiration: "",
  power: "",
  torque: "",
  transmission: "",
  drivetrain: "",
  weight: "",
  fuelType: ""
});

export const normalizeVehicleSpecs = (specs = {}) => {
  const normalizedSpecs = createEmptyVehicleSpecs();
  const sourceSpecs = specs || {};

  VEHICLE_SPEC_FIELDS.forEach(({ key }) => {
    const value = sourceSpecs[key];

    normalizedSpecs[key] =
      value === null || value === undefined ? "" : String(value).trim();
  });

  return normalizedSpecs;
};

const normalizeStage = (stage = {}) => ({
  ...stage,
  specs: normalizeVehicleSpecs(stage.specs)
});

export const createStageFromCar = (car = {}) => {
  const stageId = car.currentStageId || `stage-${Date.now()}`;

  return normalizeStage({
    id: stageId,
    name: car.stageName || "Original Build",
    isCurrent: true,
    createdAt: car.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),

    images: car.images || [],
    mods: car.mods || [],
    timeline: car.timeline || [],
    maintenance: car.maintenance || [],

    specs: normalizeVehicleSpecs(car.specs),

    performanceMods: car.performanceMods || car.engineMods || "",
    suspensionMods: car.suspensionMods || "",
    wheelTireMods: car.wheelTireMods || car.wheelMods || "",
    exteriorMods: car.exteriorMods || "",
    interiorMods: car.interiorMods || "",
    miscMods: car.miscMods || "",

    notes: car.stageNotes || ""
  });
};

export const normalizeCarStages = (car = {}) => {
  const normalizedOemSpecs = normalizeVehicleSpecs(car.oemSpecs);

  if (car.stages && car.stages.length > 0 && car.currentStageId) {
    return {
      ...car,
      oemSpecs: normalizedOemSpecs,
      stages: car.stages.map(normalizeStage)
    };
  }

  const stage = createStageFromCar(car);

  return {
    ...car,
    oemSpecs: normalizedOemSpecs,
    currentStageId: stage.id,
    stages: [stage]
  };
};

export const getCurrentStage = (car = {}) => {
  const normalizedCar = normalizeCarStages(car);

  return (
    normalizedCar.stages.find(
      (stage) => stage.id === normalizedCar.currentStageId
    ) || normalizedCar.stages[0]
  );
};

export const mergeCurrentStageIntoCar = (car = {}, stageUpdates = {}) => {
  const normalizedCar = normalizeCarStages(car);
  const currentStage = getCurrentStage(normalizedCar);

  const updatedStage = normalizeStage({
    ...currentStage,
    ...stageUpdates,
    specs: normalizeVehicleSpecs(stageUpdates.specs || currentStage.specs),
    updatedAt: new Date().toISOString()
  });

  return {
    ...normalizedCar,
    oemSpecs: normalizeVehicleSpecs(normalizedCar.oemSpecs),

    // Keep legacy fields for now so CarPage, Discover, ShowCard, etc. do not break.
    images: updatedStage.images || [],
    mods: updatedStage.mods || [],
    timeline: updatedStage.timeline || [],
    maintenance: updatedStage.maintenance || [],

    performanceMods: updatedStage.performanceMods || "",
    suspensionMods: updatedStage.suspensionMods || "",
    wheelTireMods: updatedStage.wheelTireMods || "",
    exteriorMods: updatedStage.exteriorMods || "",
    interiorMods: updatedStage.interiorMods || "",
    miscMods: updatedStage.miscMods || "",

    stages: normalizedCar.stages.map((stage) =>
      stage.id === updatedStage.id ? updatedStage : normalizeStage(stage)
    ),

    lastUpdated: new Date().toISOString()
  };
};
